import { Helmet } from 'react-helmet-async';
import { 
  SEOData, 
  generateTitle, 
  generateDescription, 
  generateCanonical,
  SITE_NAME,
  SITE_URL,
  DEFAULT_OG_IMAGE
} from '@/lib/seo';

interface SEOHeadProps extends Partial<SEOData> {
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

export function SEOHead({
  title,
  description,
  canonical,
  ogImage,
  ogType = 'website',
  noIndex = false,
  keywords,
  jsonLd
}: SEOHeadProps) {
  const pageTitle = generateTitle(title);
  const pageDescription = description ? generateDescription(description) : 
    'Buy premium indoor plants online in India. Self-watering pots, expert plant care, and free delivery. 7-day health guarantee on all plants.';
  const canonicalUrl = canonical ? generateCanonical(canonical) : undefined;
  const ogImageUrl = ogImage || DEFAULT_OG_IMAGE;

  // Handle multiple JSON-LD schemas
  const jsonLdScripts = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      {keywords && keywords.length > 0 && (
        <meta name="keywords" content={keywords.join(', ')} />
      )}
      {noIndex && <meta name="robots" content="noindex, nofollow" />}
      
      {/* Canonical URL */}
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
      
      {/* Open Graph Tags */}
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:type" content={ogType} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:image" content={ogImageUrl} />
      <meta property="og:image:alt" content={title || SITE_NAME} />
      {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
      <meta property="og:locale" content="en_IN" />
      
      {/* Twitter Card Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDescription} />
      <meta name="twitter:image" content={ogImageUrl} />
      
      {/* Additional SEO Tags */}
      <meta name="author" content={SITE_NAME} />
      <meta name="geo.region" content="IN" />
      <meta name="geo.placename" content="India" />
      
      {/* JSON-LD Structured Data */}
      {jsonLdScripts.map((schema, index) => (
        <script 
          key={index}
          type="application/ld+json"
        >
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
}

export default SEOHead;
